import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useParams } from 'react-router-dom';
import { moduleRegistry } from '@/config/module-registry.config';
import AppSidebar from '@/components/AppSidebar';
import TopNav from '@/components/TopNav';

interface AppLayoutProps {
  children: React.ReactNode;
}

/**
 * Three-zone layout: TopNav (fixed top), Sidebar (left), ContentArea (remaining space).
 *
 * Wraps everything in SidebarProvider so sidebar state is shared between
 * TopNav (toggle controls) and AppSidebar. TooltipProvider enables tooltips
 * on collapsed sidebar items.
 *
 * Modules with `fullWidth: true` in the registry skip the max-w-5xl constraint.
 */
export default function AppLayout({ children }: AppLayoutProps) {
  const { moduleId } = useParams<{ moduleId: string }>();
  const activeModule = moduleId ? moduleRegistry.find((m) => m.id === moduleId) : null;
  const isFullWidth = activeModule?.fullWidth === true;

  return (
    <TooltipProvider>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <TopNav />
          <main className="flex min-h-0 flex-1 flex-col overflow-auto p-4">
            <div className={`mx-auto w-full flex-1 ${isFullWidth ? '' : 'max-w-5xl'}`}>{children}</div>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}
